import React, { useState } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';

const ConflictModal = ({ show, handleClose, conflictInfo, handleForceAction }) => {
  const [showWarning, setShowWarning] = useState(false);
  
  if (!conflictInfo) return null;

  const formatTime = (time) => {
    if (!time) return '';
    return time.includes(':')
      ? new Date(`2000-01-01T${time}`).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        })
      : time;
  };

  const handleForceClick = () => {
    setShowWarning(true);
  };

  const handleConfirmForce = () => {
    setShowWarning(false);
    handleForceAction(conflictInfo.type === 'TIME_CONFLICT' ? 'force_schedule' : 'force_swap');
  };

  const renderConflictDetails = () => {
    const { type, student, existingStudent, timeSlot, sourceTimeSlot, conflictDetails } = conflictInfo;

    const proposedTime = timeSlot
      ? `${formatTime(timeSlot.startTime)}-${formatTime(timeSlot.endTime)}`
      : 'the selected time';

    switch (type) {
      case 'TIME_CONFLICT':
        return (
          <>
            <Alert variant="warning">
              <Alert.Heading>Time Conflict Detected</Alert.Heading>
              <p>
                Scheduling <strong>{student.name}</strong> at {proposedTime} would create a conflict
                with their existing schedule.
              </p>
            </Alert>

            <h6>Conflict Details:</h6>
            <ul className="list-unstyled">
              {Array.isArray(conflictDetails) && conflictDetails.length > 0 ? (
                conflictDetails.map((conflict, index) => (
                  <li key={index} className="mb-2">
                    <span className="text-danger">• </span>
                    {conflict.type === 'INTERVIEW'
                      ? `Interview with ${conflict.company_name} from ${conflict.start_time} to ${conflict.end_time} (Panel ${conflict.panel_number}, ${conflict.panel_place})`
                      : `=>${conflict.company_name} from ${conflict.start_time} to ${conflict.end_time}`}
                  </li>
                ))
              ) : (
                <li className="mb-2">
                  <span className="text-danger">• </span>
                  Unable to retrieve specific conflict details
                </li>
              )}
            </ul>
          </>
        );

      case 'SWAP_CONFLICT':
        const sourceTime = sourceTimeSlot
          ? `${formatTime(sourceTimeSlot.startTime)}-${formatTime(sourceTimeSlot.endTime)}`
          : 'the original time';
        return (
          <>
            <Alert variant="warning">
              <Alert.Heading>Swap Conflict Detected</Alert.Heading>
              <p>
                Swapping <strong>{student.name}</strong> to {proposedTime} would move{' '}
                <strong>{existingStudent.name}</strong> to {sourceTime}, creating a conflict with{' '}
                {existingStudent.name}’s existing schedule.
              </p>
            </Alert>

            <h6>Conflict Details for {existingStudent.name}:</h6>
            <ul className="list-unstyled">
              {Array.isArray(conflictDetails) && conflictDetails.length > 0 ? (
                conflictDetails.map((conflict, index) => (
                  <li key={index} className="mb-2">
                    <span className="text-danger">• </span>
                    {conflict.type === 'INTERVIEW'
                      ? `Interview with ${conflict.company_name} from ${conflict.start_time} to ${conflict.end_time} (Panel ${conflict.panel_number}, ${conflict.panel_place})`
                      : `=> ${conflict.company_name} from ${conflict.start_time} to ${conflict.end_time}`}
                  </li>
                ))
              ) : (
                <li className="mb-2">
                  <span className="text-danger">• </span>
                  Unable to retrieve specific conflict details
                </li>
              )}
            </ul>
          </>
        );

      default:
        return <p>Unknown conflict type</p>;
    }
  };

  const renderWarningModal = () => {
    const { type, student, existingStudent, timeSlot, sourceTimeSlot, conflictDetails } = conflictInfo;
    const proposedTime = timeSlot
      ? `${formatTime(timeSlot.startTime)}-${formatTime(timeSlot.endTime)}`
      : 'the selected time';

    let actionDescription = '';
    let consequences = [];

    if (type === 'TIME_CONFLICT') {
      actionDescription = `force schedule ${student.name} at ${proposedTime}`;
      consequences = [
        `✓ ${student.name} will be scheduled at ${proposedTime}`
      ];
      
      if (Array.isArray(conflictDetails)) {
        conflictDetails.forEach((conflict, index) => {
          consequences.push(
            `→ ${conflict.company_name || 'Unknown company'} interview from ${conflict.start_time} to ${conflict.end_time} will be moved to UNSCHEDULED`
          );
        });
      }
    } else if (type === 'SWAP_CONFLICT') {
      const sourceTime = sourceTimeSlot
        ? `${formatTime(sourceTimeSlot.startTime)}-${formatTime(sourceTimeSlot.endTime)}`
        : 'original time';
      
      actionDescription = `force swap ${student.name} with ${existingStudent?.name}`;
      consequences = [
        `✓ ${student.name} will be scheduled at ${proposedTime}`,
        `✓ ${existingStudent?.name} will be moved to ${sourceTime}`
      ];
      
      if (Array.isArray(conflictDetails)) {
        conflictDetails.forEach((conflict, index) => {
          consequences.push(
            `→ ${existingStudent?.name}'s conflicting ${conflict.company_name || 'Unknown company'} interview from ${conflict.start_time} to ${conflict.end_time} will be moved to UNSCHEDULED`
          );
        });
      }
    }

    return (
      <Modal show={showWarning} onHide={() => setShowWarning(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-warning">⚠️ Force {type === 'TIME_CONFLICT' ? 'Schedule' : 'Swap'} Warning</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <Alert.Heading>Confirm Force Action</Alert.Heading>
            <p>
              <strong>You are about to {actionDescription}.</strong>
            </p>
            <hr />
            <h6 className="text-danger">This will cause the following changes:</h6>
            <ul className="mb-0">
              {consequences.map((consequence, index) => (
                <li key={index} className={consequence.startsWith('→') ? 'text-danger' : 'text-success'}>
                  {consequence}
                </li>
              ))}
            </ul>
          </Alert>
          <div className="bg-light p-3 rounded mt-3">
            <small className="text-muted">
              <strong>Note:</strong> Interviews moved to UNSCHEDULED will appear in the "Unscheduled Students" section 
              and can be rescheduled to other available time slots.
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowWarning(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmForce}>
            Yes, Force {type === 'TIME_CONFLICT' ? 'Schedule' : 'Swap'}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  if (showWarning) {
    return renderWarningModal();
  }

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Scheduling Conflict</Modal.Title>
      </Modal.Header>
      <Modal.Body>{renderConflictDetails()}</Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="warning"
          onClick={handleForceClick}
        >
          Force {conflictInfo.type === 'TIME_CONFLICT' ? 'Schedule' : 'Swap'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConflictModal;